<template>
	<div
		class="contained-section relative mb-4 mt-8 flex flex-col gap-6 py-4 sm:mb-10 lg:flex-row xl:p-0"
	>
		<div class="flex w-full flex-col gap-2 lg:w-1/2 xl:w-2/3 xl:px-8">
			<p class="text-xl text-gray-700 md:text-center lg:text-left">
				Carbar - All inclusive car subscription
			</p>
			<h1
				class="mb-0 mt-4 bg-gradient-to-br from-teal-400 to-sky-600 bg-clip-text pb-1 leading-none text-transparent md:mt-0 md:px-20 md:text-center lg:px-0 lg:text-left"
			>
				Drive forward with subscription flexibility and novated savings
			</h1>
			<p class="mt-2 text-2xl md:text-center lg:text-left">
				Empowering your drive. Simplified solutions for seamless rides.
			</p>

			<DoubleCta class="mt-8 md:mx-auto lg:mx-0" :is-stacked="false">
				<template #primary>
					<CarbarLink font-size="text-base" href="/cars/subscribe">
						<CarbarButton>
							{{ `Browse ${totalVehiclesCount} cars` }}
						</CarbarButton>
					</CarbarLink>
				</template>
				<template #secondary>
					<CarbarLink
						:underline="false"
						href="/car-subscription"
						class="group w-fit"
					>
						<div
							class="flex h-full items-center justify-center gap-2 px-4 py-2"
						>
							<IconPage
								class="h-[22px] w-[22px] text-gray-500 transition-colors duration-300 ease-in-out group-hover:text-blue-400"
							/>
							<span
								class="leading-none transition-colors duration-300 ease-in-out group-hover:text-blue-400"
							>
								How car subscription works?
							</span>
						</div>
					</CarbarLink>
				</template>
			</DoubleCta>

			<div
				class="mt-4 flex flex-col items-center justify-center gap-2 sm:flex-row md:mt-2 lg:justify-start"
			>
				<CarbarLink
					class="w-full md:w-auto"
					font-size="text-base"
					href="/cars/subscribe?conditions[0]=new"
					:underline="false"
				>
					<div
						class="group flex items-center justify-center gap-2 rounded border border-gray-400 px-4 py-3 transition-colors duration-300 ease-in-out hover:border-sky-600 hover:bg-sky-600"
					>
						<CarbarImage
							image="img/common/img-volvo-small.webp"
							alt-text="Browse new"
							image-width-class="80px"
							sizes="72px"
							densities="x1 x2"
							loading="eager"
							:preload="true"
						/>
						<div class="flex flex-col">
							<div
								class="text-lg leading-none text-blue-400 transition-colors duration-300 ease-in-out group-hover:text-white"
							>
								Browse new
							</div>
							<div
								v-if="hasNewVehiclesCount"
								class="text-sm transition-colors duration-300 ease-in-out group-hover:text-white"
							>
								{{ totalNewVehiclesCount }} cars
							</div>
						</div>
					</div>
				</CarbarLink>
				<CarbarLink
					class="w-full md:w-auto"
					font-size="text-base"
					href="/cars/subscribe?conditions[0]=used"
					:underline="false"
				>
					<div
						class="group flex items-center justify-center gap-2 rounded border border-gray-400 px-4 py-3 transition-colors duration-300 ease-in-out hover:border-sky-600 hover:bg-sky-600"
					>
						<CarbarImage
							image="img/common/img-golf-small.webp"
							alt-text="Browse new"
							image-width-class="80px"
							sizes="72px"
							densities="x1 x2"
							loading="eager"
							:preload="true"
						/>
						<div class="flex flex-col">
							<div
								class="text-lg leading-none text-blue-400 transition-colors duration-300 ease-in-out group-hover:text-white"
							>
								Browse used
							</div>
							<div
								v-if="hasUsedVehiclesCount"
								class="text-sm transition-colors duration-300 ease-in-out group-hover:text-white"
							>
								{{ totalUsedVehiclesCount }} cars
							</div>
						</div>
					</div>
				</CarbarLink>
			</div>

			<TermsAndConditions
				:is-full-width="false"
				custom-class="text-center md:text-center lg:text-left mt-4 md:mx-auto lg:mx-0"
			>
				<CarbarLink
					text="Terms & conditions"
					text-color="text-gray-625"
					font-size="text-xsm"
					class="cursor-pointer"
					href="/terms-and-conditions"
				/>
				apply
			</TermsAndConditions>
		</div>

		<div class="relative mb-8 lg:w-1/2 xl:w-1/3">
			<div
				class="relative flex h-full w-full flex-col items-center lg:items-end"
			>
				<CarbarImage
					image="img/home/img-bg.webp"
					alt-text="carbar city"
					sizes="581px"
					image-width-class="w-auto sm:w-[320px] lg:w-[340px] xl:w-[400px]"
					image-height-class="h-[120px] sm:h-[140px] xl:h-[200px]"
					class="rounded object-cover text-center sm:ml-[60px] lg:mt-[50px] xl:mt-5"
					densities="x1 x2"
					loading="eager"
					:preload="true"
				/>
			</div>
		</div>
		<CarbarImage
			image="img/home/img-fg.webp"
			alt-text="carbar cars"
			image-width-class="w-[300px] sm:w-[360px] md:w-[360px] lg:w-[400px] xl:w-[500px]"
			image-height-class="h-auto"
			sizes="300px sm:360px lg:400px xl:500px"
			class="absolute -bottom-[50px] sm:-bottom-[70px] sm:left-[90px] md:left-[160px] lg:bottom-auto lg:left-auto lg:right-[50px] lg:top-[100px] xl:top-[70px]"
			densities="x1 x2"
			loading="eager"
			:preload="true"
		/>
	</div>
</template>

<script setup lang="ts">
import IconPage from '@/assets/svg/header/ico-page.svg'
import CarbarButton from '@/components/buttons/CarbarButton.vue'
import DoubleCta from '@/components/buttons/DoubleCta.vue'
import CarbarImage from '@/components/CarbarImage.vue'
import TermsAndConditions from '@/components/fineprint/TermsAndConditions.vue'
import CarbarLink from '@/components/links/CarbarLink.vue'
import { useCarbarStore } from '@/stores/carbar'

const carbar = useCarbarStore()

const totalVehiclesCount = computed(() =>
	carbar.vehicleCount.subscription > 0 ? carbar.vehicleCount.subscription : ''
)

const hasNewVehiclesCount = computed(() => carbar.vehicleCount.new > 0)

const totalNewVehiclesCount = computed(() =>
	carbar.vehicleCount.new > 0 ? carbar.vehicleCount.new : ''
)

const hasUsedVehiclesCount = computed(() => carbar.vehicleCount.new > 0)

const totalUsedVehiclesCount = computed(() =>
	carbar.vehicleCount.used > 0 ? carbar.vehicleCount.used : ''
)
</script>
